'use client'

import posthog from 'posthog-js'
import { isNoiseError } from '@/lib/error-noise'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { useEffect, Suspense, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useUser } from '@clerk/nextjs'
import { useQuery } from '@/lib/convex-react'
import { api } from '@/convex/_generated/api'

// Only initialize PostHog if key is provided
const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST

// Track if PostHog has been initialized
let posthogInitialized = false

function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (pathname && posthogKey && typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      let url = window.origin + pathname
      if (searchParams && searchParams.toString()) {
        url = url + `?${searchParams.toString()}`
      }
      posthog.capture('$pageview', {
        $current_url: url,
      })
    }
  }, [pathname, searchParams])

  return null
}

// Link PostHog (events + session replays) to the Clerk account so a person
// profile is created and replays are searchable by user. Pre-login anonymous
// events on the same device are merged into the person at identify time.
function PostHogIdentify() {
  const { user, isSignedIn, isLoaded } = useUser()
  // ENG-30 (#207): `users.isTestUser` is the single flag that excludes an
  // account from admin stats AND from PostHog — set it as a person property
  // so Growth can filter persons/cohorts on `isTestUser` (see
  // docs/analytics/posthog-test-user-exclusion.md).
  const analyticsIdentity = useQuery(api.users.getAnalyticsIdentity, isSignedIn ? {} : 'skip')
  const isTestUser = analyticsIdentity?.isTestUser

  const email = user?.primaryEmailAddress?.emailAddress
  const name = user?.fullName ?? undefined

  useEffect(() => {
    if (!isLoaded) return
    if (isSignedIn && user) {
      // identify() is a no-op if already identified with the same id, but
      // skip it anyway to avoid re-sending person properties on every mount
      if (posthog.get_distinct_id() !== user.id) {
        posthog.identify(user.id, { email, name, ...(isTestUser === undefined ? {} : { isTestUser }) })
      } else if (isTestUser === true) {
        // Flag flipped (or identity arrived after identify): stamp the person.
        posthog.setPersonProperties({ isTestUser: true })
      }
    } else if (posthog._isIdentified()) {
      // Signed out: reset so the next user on this device isn't attributed
      // to the previous account. Only when identified — resetting anonymous
      // visitors would discard their device id on every page load.
      posthog.reset()
    }
  }, [isLoaded, isSignedIn, user, email, name, isTestUser])

  return null
}

// Internal-traffic filter: admin/analytics accounts pollute every ratio metric
// (174 /admin sessions in 28d), so their browsers opt out of capture entirely.
// Opt back in only for confirmed regular users (role === null) — never on
// localhost, where the init-time opt-out must stay in force.
// ENG-30 (#207): accounts flagged `users.isTestUser` are treated exactly like
// internal accounts here — their browsers opt out of capture — so the same
// flag that hides them from admin stats also keeps them out of PostHog.
// Same noise list as Sentry (`lib/error-noise.ts`) so both sinks agree.
export function dropNoiseExceptions<T extends { event?: string; properties?: Record<string, unknown> } | null>(event: T): T | null {
  if (!event || event.event !== '$exception') return event
  const props = event.properties || {}
  const values = props.$exception_values
  const list = props.$exception_list
  const first = Array.isArray(list) ? (list[0] as { value?: unknown } | undefined) : undefined
  const message =
    (Array.isArray(values) && typeof values[0] === 'string' ? values[0] : undefined) ??
    (typeof first?.value === 'string' ? first.value : undefined) ??
    (typeof props.$exception_message === 'string' ? props.$exception_message : undefined)
  return isNoiseError(message) ? null : event
}

function PostHogInternalFilter() {
  const { isSignedIn } = useUser()
  const identity = useQuery(api.users.getAnalyticsIdentity, isSignedIn ? {} : 'skip')

  useEffect(() => {
    if (typeof window === 'undefined' || window.location.hostname === 'localhost') return
    if (identity === undefined) return // signed out or still loading: leave state untouched
    const internal = identity !== null && (identity.role === 'admin' || identity.role === 'analytics' || identity.isTestUser)
    if (internal) {
      if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing()
    } else if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing()
    }
  }, [identity])

  return null
}

export function PostHogProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isReady, setIsReady] = useState(posthogInitialized)

  // Defer PostHog initialization to after hydration (bundle-defer-third-party)
  useEffect(() => {
    if (!posthogKey || !posthogHost || posthogInitialized) {
      if (posthogInitialized) setIsReady(true)
      return
    }

    // Initialize PostHog after hydration to avoid blocking initial render
    posthog.init(posthogKey, {
      // Reverse proxy through our domain (next.config /ingest rewrites) so ad
      // blockers don't drop events; ui_host keeps toolbar/app links working.
      api_host: '/ingest',
      ui_host: posthogHost.replace('.i.posthog.com', '.posthog.com'),
      person_profiles: 'identified_only',
      capture_pageview: false, // We'll manually capture pageviews
      capture_pageleave: true,
      // Inputs are recorded in replays (product decision 2026-07: prompt
      // visibility for AI chat). PostHog still force-masks password fields;
      // the privacy & cookie policies disclose this behavior — keep in sync
      session_recording: {
        maskAllInputs: false,
      },
      // Filter out localhost / dev traffic to keep production analytics clean
      autocapture: typeof window !== 'undefined' && window.location.hostname === 'localhost' ? false : undefined,
      opt_out_capturing_by_default: typeof window !== 'undefined' && window.location.hostname === 'localhost',
      before_send: dropNoiseExceptions,
    })
    posthogInitialized = true
    setIsReady(true)
  }, [])

  // If PostHog isn't configured, just render children
  if (!posthogKey) {
    return <>{children}</>
  }

  // Keep the element tree shape identical before and after init. Switching
  // from `<>{children}</>` to `<PHProvider>…{children}</PHProvider>` once
  // ready changed the parent type/position of `children`, so React unmounted
  // and remounted the whole app subtree after hydration on every page load —
  // state lost, effects re-run, and next-themes' inline <script> re-created
  // client-side ("Encountered a script tag while rendering React component").
  // PHProvider only exposes the client object; rendering it before init is
  // safe, and the helpers that actually call PostHog stay gated on isReady.
  return (
    <PHProvider client={posthog}>
      {isReady ? (
        <>
          <Suspense fallback={null}>
            <PostHogPageView />
          </Suspense>
          <PostHogIdentify />
          <PostHogInternalFilter />
        </>
      ) : null}
      {children}
    </PHProvider>
  )
}
